import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Eye } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import TicketReceiptCard, { formatManualTicketId } from "../components/TicketReceiptCard";
import { formatExactSection, resolveStoredSeatArea } from "../lib/seatSectionDisplay";
import { buildTicketReceiptPdf } from "../lib/ticketReceiptPdf";
import { resolveEventPolicyHtml } from "../lib/venuePolicyHtml";
import { resolveTicketSectionColor } from "../lib/sectionColor";
import { fetchVenueSectionColors } from "../lib/venuesApi";
import { matchesKeyword, sanitizeSearchInput, SEARCH_NO_RESULTS_MESSAGE } from "../utils/searchUtils";
import { uniqueIds } from "../lib/adminPagination";

const ORDER_PAGE_SIZE = 10;
const TICKET_FETCH_BATCH = 1000;

const parseEventDateTime = (eventDate, eventTime) => {
  if (!eventDate) return null;
  const parsed = new Date(`${eventDate}T${eventTime || "00:00"}`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const getSecondKey = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value || "unknown");
  parsed.setMilliseconds(0);
  return parsed.toISOString();
};

const formatOrderCode = (idValue) => {
  const source = String(idValue || "000").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return `#ORD-${source.slice(0, 6).padEnd(3, "0")}`;
};

const isArchivedEventStatus = (status) => String(status || "").trim().toLowerCase() === "archived";

const getTicketStatusMeta = (ticket) => {
  if (ticket.refundedAt) {
    return {
      key: "refunded",
      label: "Refunded",
      badgeClassName: "bg-violet-500/15 text-violet-300 border-violet-500/25",
    };
  }

  if (ticket.isValidated) {
    return {
      key: "used",
      label: "Used",
      badgeClassName: "bg-blue-500/15 text-blue-300 border-blue-500/25",
    };
  }

  const eventArchived = isArchivedEventStatus(ticket.eventStatus);
  const eventDatePassed = Boolean(ticket.eventDateTime && ticket.eventDateTime.getTime() < Date.now());
  const showtimeStillFuture = Boolean(ticket.eventDateTime && ticket.eventDateTime.getTime() >= Date.now());

  if (eventArchived || eventDatePassed) {
    const cancelledEarly = eventArchived && showtimeStillFuture;
    return {
      key: "past",
      label: cancelledEarly ? "Cancelled" : "Past",
      badgeClassName: cancelledEarly
        ? "bg-amber-500/15 text-amber-300 border-amber-500/25"
        : "bg-white/10 text-white/60 border-white/15",
    };
  }

  return {
    key: "active",
    label: "Active",
    badgeClassName: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  };
};

const getSeatCacheStorageKey = (userId) => `echo-jham-ticket-seat-cache:${userId}`;

const isMissingTicketColumnError = (error) => {
  const message = String(error?.message || "").toLowerCase();
  return String(error?.code || "") === "42703" || message.includes("refunded_at") || message.includes("attendee_name");
};

const fetchAllBuyerTicketRows = async (userId) => {
  const selectCandidates = [
    "id, qr_code, seat_label, created_at, is_validated, validated_at, ticket_tier_id, seat_area, attendee_name, refunded_at",
    "id, qr_code, seat_label, created_at, is_validated, validated_at, ticket_tier_id, seat_area, attendee_name",
    "id, qr_code, seat_label, created_at, is_validated, validated_at, ticket_tier_id, seat_area, refunded_at",
    "id, qr_code, seat_label, created_at, is_validated, validated_at, ticket_tier_id, seat_area",
  ];

  const runPage = (columns, from, to) =>
    supabase
      .from("tickets")
      .select(columns, from === 0 ? { count: "exact" } : undefined)
      .eq("buyer_id", userId)
      .order("created_at", { ascending: false })
      .range(from, to);

  let workingColumns = "";
  let firstPage = null;
  for (const columns of selectCandidates) {
    const result = await runPage(columns, 0, TICKET_FETCH_BATCH - 1);
    if (!result.error) {
      workingColumns = columns;
      firstPage = result;
      break;
    }
    if (!isMissingTicketColumnError(result.error)) return result;
    firstPage = result;
  }

  if (!firstPage || firstPage.error || !workingColumns) return firstPage;

  const rows = [...(firstPage.data || [])];
  const total = Number(firstPage.count || rows.length);
  let offset = rows.length;
  while (offset < total) {
    const nextPage = await runPage(workingColumns, offset, offset + TICKET_FETCH_BATCH - 1);
    if (nextPage.error) return nextPage;
    const batch = nextPage.data || [];
    rows.push(...batch);
    if (!batch.length) break;
    offset += batch.length;
  }

  return { data: rows, error: null, count: total };
};

const MyTickets = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState("");
  const [retryingFetch, setRetryingFetch] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [orders, setOrders] = useState([]);
  const [shownCount, setShownCount] = useState(ORDER_PAGE_SIZE);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchTickets = useCallback(async ({ showRetryState = false } = {}) => {
    if (showRetryState) {
      setRetryingFetch(true);
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user?.id) {
        navigate("/login");
        return;
      }

      let cachedSeatLabels = {};
      if (typeof window !== "undefined") {
        try {
          const rawCache = window.localStorage.getItem(getSeatCacheStorageKey(user.id));
          const parsedCache = rawCache ? JSON.parse(rawCache) : {};
          if (parsedCache && typeof parsedCache === "object" && !Array.isArray(parsedCache)) {
            cachedSeatLabels = parsedCache;
          }
        } catch (storageError) {
          console.warn("Unable to read cached seat labels:", storageError);
        }
      }

      const ticketsResult = await fetchAllBuyerTicketRows(user.id);
      const { data: ticketsData, error: ticketsError } = ticketsResult;
      const tierIds = uniqueIds(ticketsData, "ticket_tier_id");

      let tiersResult = tierIds.length
        ? await supabase.from("ticket_tiers").select("id, event_id, name, price, section, seat_areas, color").in("id", tierIds)
        : { data: [], error: null };

      if (
        tiersResult.error &&
        (String(tiersResult.error.code || "") === "42703" ||
          String(tiersResult.error.message || "").toLowerCase().includes("color"))
      ) {
        tiersResult = await supabase.from("ticket_tiers").select("id, event_id, name, price, section, seat_areas").in("id", tierIds);
      }

      let { data: tiersData, error: tiersError } = tiersResult;
      const eventIds = uniqueIds(tiersData, "event_id");
      let eventsResult = eventIds.length
        ? await supabase.from("events").select("id, title, image_url, date, time, venue, address, venue_policy_snapshot, description, venue_id, seat_layout_snapshot, status").in("id", eventIds)
        : { data: [], error: null };
      let eventsData = eventsResult.data;
      let eventsError = eventsResult.error;
      if (eventsError) {
        const fallbackWithStatus = await supabase.from("events").select("id, title, image_url, date, time, venue, status").in("id", eventIds);
        if (!fallbackWithStatus.error) {
          eventsData = fallbackWithStatus.data;
          eventsError = null;
        } else {
          const fallbackEvents = await supabase.from("events").select("id, title, image_url, date, time, venue").in("id", eventIds);
          eventsData = fallbackEvents.data;
          eventsError = fallbackEvents.error;
        }
      }

      if (ticketsError || tiersError || eventsError) {
        const combinedError = ticketsError || tiersError || eventsError;
        console.error("Failed to load user tickets:", combinedError);
        setServerError(true);
        setServerErrorMessage(combinedError?.message || "Unable to load your tickets right now. Please retry.");
        setOrders([]);
        setLoading(false);
        if (showRetryState) {
          setRetryingFetch(false);
        }
        return;
      }

      const tiersById = {};
      (tiersData || []).forEach((tier) => {
        tiersById[tier.id] = tier;
      });

      const eventsById = {};
      (eventsData || []).forEach((event) => {
        eventsById[event.id] = event;
      });

      const venueSections = await fetchVenueSectionColors(
        supabase,
        (eventsData || []).map((event) => event.venue_id)
      );

      const buyerName = String(user.user_metadata?.full_name || user.user_metadata?.name || "").trim();
      const buyerEmail = String(user.email || "").trim();

      const normalizedTickets = (ticketsData || [])
        .map((row) => {
          const tier = tiersById[row.ticket_tier_id] || null;
          const event = tier ? eventsById[tier.event_id] || null : null;
          if (!event?.id) return null;

          const eventDateTime = parseEventDateTime(event.date, event.time);
          const status = getTicketStatusMeta({
            isValidated: row.is_validated === true,
            eventDateTime,
            eventStatus: event.status || "",
            refundedAt: row.refunded_at || null,
          });

          const cachedSeatLabel = cachedSeatLabels[String(row.id)]?.seatLabel || "";

          return {
            id: row.id,
            manualTicketId: formatManualTicketId(row.id),
            qrCode: row.qr_code || "",
            seatLabel: row.seat_label || cachedSeatLabel || "",
            attendeeName: String(row.attendee_name || "").trim() || buyerName || buyerEmail || "Guest",
            attendeeEmail: buyerEmail || "-",
            purchasedAt: row.created_at,
            isValidated: row.is_validated === true,
            validatedAt: row.validated_at || null,
            refundedAt: row.refunded_at || null,
            tierName: tier?.name || "General Admission",
            sectionDisplay: formatExactSection(
              tier?.section || tier?.name,
              resolveStoredSeatArea({
                seatArea: row.seat_area,
                seatLabel: row.seat_label,
                tierSeatAreas: tier?.seat_areas,
              })
            ),
            sectionColor: resolveTicketSectionColor({
              tier,
              event,
              venueSections,
            }),
            price: Number(tier?.price || 0),
            eventId: event.id,
            eventTitle: event.title || "Untitled Event",
            eventImage: event.image_url || "",
            eventDate: event.date || "",
            eventTime: event.time || "",
            eventVenue: event.venue || "-",
            eventAddress: event.address || "",
            policyHtml: resolveEventPolicyHtml(event),
            eventDateTime,
            statusKey: status.key,
            statusLabel: status.label,
            statusBadgeClassName: status.badgeClassName,
          };
        })
        .filter(Boolean);

      const grouped = new Map();

      normalizedTickets.forEach((ticket) => {
        const orderKey = `${ticket.statusKey}|${ticket.eventId}|${getSecondKey(ticket.purchasedAt)}`;

        if (!grouped.has(orderKey)) {
          grouped.set(orderKey, {
            id: orderKey,
            orderCode: formatOrderCode(ticket.id),
            eventId: ticket.eventId,
            eventTitle: ticket.eventTitle,
            eventImage: ticket.eventImage,
            eventDate: ticket.eventDate,
            eventTime: ticket.eventTime,
            eventVenue: ticket.eventVenue,
            eventDateTime: ticket.eventDateTime,
            purchasedAt: ticket.purchasedAt,
            statusKey: ticket.statusKey,
            statusLabel: ticket.statusLabel,
            statusBadgeClassName: ticket.statusBadgeClassName,
            tickets: [],
          });
        }

        grouped.get(orderKey).tickets.push(ticket);
      });

      setOrders(Array.from(grouped.values()));
      setServerError(false);
      setServerErrorMessage("");
      setLoading(false);
    } catch (error) {
      console.error("Failed to load user tickets:", error);
      setServerError(true);
      setServerErrorMessage("Unable to load your tickets right now. Please retry.");
      setOrders([]);
      setLoading(false);
    } finally {
      if (showRetryState) {
        setRetryingFetch(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const { activeOrders, usedOrders, pastOrders, refundedOrders } = useMemo(() => {
    const buckets = {
      active: [],
      used: [],
      past: [],
      refunded: [],
    };

    orders.forEach((order) => {
      if (order.statusKey === "refunded") {
        buckets.refunded.push(order);
      } else if (order.statusKey === "used") {
        buckets.used.push(order);
      } else if (order.statusKey === "past" || order.statusKey === "expired") {
        buckets.past.push(order);
      } else {
        buckets.active.push(order);
      }
    });

    buckets.active.sort((a, b) => (a.eventDateTime?.getTime() || 0) - (b.eventDateTime?.getTime() || 0));
    buckets.used.sort((a, b) => (b.eventDateTime?.getTime() || 0) - (a.eventDateTime?.getTime() || 0));
    buckets.past.sort((a, b) => (b.eventDateTime?.getTime() || 0) - (a.eventDateTime?.getTime() || 0));
    buckets.refunded.sort((a, b) => (b.eventDateTime?.getTime() || 0) - (a.eventDateTime?.getTime() || 0));

    return {
      activeOrders: buckets.active,
      usedOrders: buckets.used,
      pastOrders: buckets.past,
      refundedOrders: buckets.refunded,
    };
  }, [orders]);

  const visibleOrders =
    activeTab === "active"
      ? activeOrders
      : activeTab === "used"
        ? usedOrders
        : activeTab === "refunded"
          ? refundedOrders
          : pastOrders;
  const ticketKeywordFromUrl = searchParams.get("q") || "";
  const sanitizedTicketSearch = sanitizeSearchInput(ticketKeywordFromUrl);
  const hasActiveTicketSearch = Boolean(ticketKeywordFromUrl.trim());

  const searchedOrders = useMemo(() => {
    if (!hasActiveTicketSearch || !sanitizedTicketSearch.isValid) {
      return visibleOrders;
    }

    return visibleOrders.filter((order) =>
      matchesKeyword(
        [
          order.eventTitle,
          order.eventVenue,
          order.orderCode,
          ...order.tickets.map((ticket) => `${ticket.tierName} ${ticket.manualTicketId}`),
        ],
        sanitizedTicketSearch.cleaned
      )
    );
  }, [hasActiveTicketSearch, sanitizedTicketSearch.cleaned, sanitizedTicketSearch.isValid, visibleOrders]);

  const displayedOrders = searchedOrders.slice(0, shownCount);
  const hasMoreOrders = shownCount < searchedOrders.length;

  useEffect(() => {
    setShownCount(ORDER_PAGE_SIZE);
  }, [activeTab, ticketKeywordFromUrl]);

  const activeCount = activeOrders.length;
  const usedCount = usedOrders.length;
  const pastCount = pastOrders.length;
  const refundedCount = refundedOrders.length;

  const emptyTabMessage =
    activeTab === "past"
      ? "No past event tickets."
      : "No tickets purchased yet.";

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const handleDownloadSinglePdf = async () => {
    if (!selectedTicket || isDownloadingPdf) return;

    try {
      setIsDownloadingPdf(true);
      const doc = await buildTicketReceiptPdf(selectedTicket);
      doc.save(`ticket-${selectedTicket.manualTicketId}.pdf`);
    } catch (error) {
      console.error(error);
      alert("Failed to generate PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white py-10">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h1 className="text-3xl font-black uppercase tracking-tight italic mb-6">My Tickets</h1>

        <div className="flex items-center gap-8 border-b border-white/15 mb-6">
          <button
            onClick={() => setActiveTab("active")}
            className={`pb-3 text-sm font-bold ${
              activeTab === "active"
                ? "text-red-500 border-b-2 border-red-500"
                : "text-white/70 hover:text-white"
            }`}
          >
            Active ({activeCount})
          </button>

          <button
            onClick={() => setActiveTab("used")}
            className={`pb-3 text-sm font-bold ${
              activeTab === "used"
                ? "text-red-500 border-b-2 border-red-500"
                : "text-white/70 hover:text-white"
            }`}
          >
            Used ({usedCount})
          </button>

          <button
            onClick={() => setActiveTab("refunded")}
            className={`pb-3 text-sm font-bold ${
              activeTab === "refunded"
                ? "text-red-500 border-b-2 border-red-500"
                : "text-white/70 hover:text-white"
            }`}
          >
            Refunded ({refundedCount})
          </button>

          <button
            onClick={() => setActiveTab("past")}
            className={`pb-3 text-sm font-bold ${
              activeTab === "past"
                ? "text-red-500 border-b-2 border-red-500"
                : "text-white/70 hover:text-white"
            }`}
          >
            Past Events ({pastCount})
          </button>
        </div>

        {loading && searchedOrders.length === 0 ? (
          <div className="py-14 text-center text-white/70 text-sm">Loading...</div>
        ) : serverError ? (
          <div className="py-14 text-center text-red-200 text-sm border border-red-500/20 rounded-xl bg-red-500/[0.06] max-w-2xl mx-auto px-6">
            <p className="font-semibold">{serverErrorMessage || "Unable to load your tickets right now."}</p>
            <button
              type="button"
              onClick={() => fetchTickets({ showRetryState: true })}
              disabled={retryingFetch}
              className="mt-5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest"
            >
              {retryingFetch ? "Retrying..." : "Retry"}
            </button>
          </div>
        ) : hasActiveTicketSearch && !sanitizedTicketSearch.isValid ? (
          <div className="py-14 text-center text-red-300 text-sm border border-red-500/20 rounded-xl bg-red-500/[0.06]">
            Please enter a valid search keyword using letters and numbers.
          </div>
        ) : searchedOrders.length === 0 ? (
          <div className="py-14 text-center text-white/70 text-sm border border-white/10 rounded-xl bg-white/[0.02]">
            {hasActiveTicketSearch
              ? SEARCH_NO_RESULTS_MESSAGE
              : emptyTabMessage}
          </div>
        ) : (
          <div className="space-y-3">
            {displayedOrders.map((order) => {
              const isExpanded = expandedOrders.has(order.id);

              return (
                <div key={order.id} className="bg-black/70 border border-white/20 rounded-md px-3 py-2.5">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={order.eventImage || "/assets/home_featured_bg.png"}
                        alt={order.eventTitle}
                        className="w-10 h-10 object-cover rounded-sm border border-white/20"
                      />

                      <div className="min-w-0">
                        <p className="text-sm font-semibold uppercase truncate">{order.eventTitle}</p>
                        <p className="text-[10px] text-red-400">
                          {order.eventDate || "-"} {order.eventTime ? `| ${order.eventTime}` : ""}
                        </p>
                        <p className="text-[10px] text-white/60 truncate">
                          {order.orderCode} • {order.tickets.length} ticket{order.tickets.length > 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>

                    <span className={`justify-self-center whitespace-nowrap min-w-[88px] text-center text-[10px] font-black uppercase tracking-wide px-3 py-1 rounded-full border ${
                      order.statusBadgeClassName || "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
                    }`}>
                      {order.statusLabel}
                    </span>

                    <button
                      onClick={() => toggleOrderExpand(order.id)}
                      className="justify-self-end shrink-0 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-wide px-4 py-2 rounded inline-flex items-center gap-1"
                    >
                      {isExpanded ? "Collapse" : "Expand"}
                      {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                      {order.tickets.map((ticket, idx) => (
                        <div
                          key={ticket.id}
                          className="bg-white/[0.04] border border-white/10 rounded px-3 py-2 flex items-center justify-between gap-3"
                        >
                          <div>
                            <p className="text-xs font-semibold text-white">Ticket #{idx + 1}</p>
                              <p className="text-[10px] text-white/70">{ticket.tierName} • {ticket.manualTicketId}</p>
                              <p className="text-[10px] text-white/60">Row - Seat: {ticket.seatLabel || "Assigned at issuance"}</p>
                              <p className="text-[10px] text-white/50 mt-1 uppercase tracking-wide">{ticket.statusLabel}</p>
                          </div>

                          <button
                            onClick={() => setSelectedTicket(ticket)}
                            className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-wide px-3 py-1.5 rounded inline-flex items-center gap-1"
                          >
                            <Eye size={12} /> View Ticket
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {hasMoreOrders ? (
              <div className="flex justify-center pt-4">
                <button
                  type="button"
                  onClick={() => setShownCount((count) => count + ORDER_PAGE_SIZE)}
                  className="min-w-[140px] px-6 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-black uppercase"
                >
                  Load More
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {selectedTicket && (
        <div className="fixed inset-x-0 top-14 bottom-10 z-50 bg-black/45 px-4 flex items-center justify-center" onClick={() => setSelectedTicket(null)}>
          <div
            className="w-[390px] max-w-[92vw] max-h-[84vh] bg-black rounded-xl overflow-hidden shadow-2xl border border-red-600/60"
            onClick={(e) => e.stopPropagation()}
          >
            {/* RED HEADER */}
            <div className="bg-red-600 text-center py-2.5 font-bold text-base sm:text-lg relative uppercase tracking-wide">
              Ticket Details
              <button
                onClick={() => setSelectedTicket(null)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/90 hover:text-white text-3xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-4 pb-4 pt-4 flex flex-col items-center overflow-y-auto no-scrollbar max-h-[calc(84vh-52px)]">
              <TicketReceiptCard
                ticket={selectedTicket}
                onContactSupport={() => {
                  setSelectedTicket(null);
                  navigate("/profile?section=support");
                }}
              />

              {!selectedTicket?.refundedAt && !/^refunded$/i.test(String(selectedTicket?.statusLabel || "")) ? (
                <button
                  onClick={handleDownloadSinglePdf}
                  disabled={isDownloadingPdf}
                  className="w-full bg-red-600 py-2 rounded-md font-bold uppercase text-xs hover:bg-red-700 disabled:opacity-60 mt-4"
                >
                  {isDownloadingPdf ? "Generating..." : "Download PDF"}
                </button>
              ) : null}

              <button
                onClick={() => setSelectedTicket(null)}
                className="w-full mt-3 border border-white/30 text-white py-2 rounded-md text-sm font-semibold hover:bg-white/10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTickets;
